import React from 'react';
import PropTypes from 'prop-types';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Edit,
  PersonAdd,
  PersonRemove,
  Visibility,
  CheckCircle,
  Cancel,
  Link,
  LinkOff
} from '@mui/icons-material';

/**
 * Action menu component for employee operations
 * @param {Object} props - Component props
 * @param {Object} props.anchorEl - Menu anchor element
 * @param {Object} props.employee - Employee record
 * @param {Function} props.onClose - Handler to close menu
 * @param {Function} props.onEdit - Handler for edit action
 * @param {Function} props.onDeactivate - Handler for deactivate action
 * @param {Function} props.onReactivate - Handler for reactivate action
 * @param {Function} props.onLinkUser - Handler for link user action
 * @param {Function} props.onUnlinkUser - Handler for unlink user action
 * @param {Function} props.onViewUser - Handler for view user action
 * @returns {React.Component} Action menu with employee operations
 */
function EmployeeActions({
  anchorEl,
  employee,
  onClose,
  onEdit,
  onDeactivate,
  onReactivate,
  onLinkUser,
  onUnlinkUser,
  onViewUser
}) {
  const isActive = employee?.isActive !== false;
  const hasUser = !!employee?.userId;

  const handleAction = (action) => {
    if (action && typeof action === 'function') {
      action(employee);
    }
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <MenuItem onClick={() => handleAction(onEdit)}>
        <ListItemIcon>
          <Edit fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Employee Details</ListItemText>
      </MenuItem>

      <Divider />

      {/* User account management */}
      {hasUser ? (
        <>
          <MenuItem onClick={() => handleAction(onViewUser)}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>View User Account</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleAction(onUnlinkUser)}>
            <ListItemIcon>
              <LinkOff fontSize="small" />
            </ListItemIcon>
            <ListItemText>Unlink from RadixDash User</ListItemText>
          </MenuItem>
        </>
      ) : (
        <MenuItem onClick={() => handleAction(onLinkUser)}>
          <ListItemIcon>
            <Link fontSize="small" />
          </ListItemIcon>
          <ListItemText>Link to RadixDash User</ListItemText>
        </MenuItem>
      )}

      <Divider />

      {/* Activation status management */}
      {isActive ? (
        <MenuItem 
          onClick={() => handleAction(onDeactivate)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Cancel fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Deactivate Employee</ListItemText>
        </MenuItem>
      ) : (
        <MenuItem 
          onClick={() => handleAction(onReactivate)}
          sx={{ color: 'success.main' }}
        >
          <ListItemIcon>
            <CheckCircle fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Reactivate Employee</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
}

EmployeeActions.propTypes = {
  anchorEl: PropTypes.object,
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    userId: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onDeactivate: PropTypes.func,
  onReactivate: PropTypes.func,
  onLinkUser: PropTypes.func,
  onUnlinkUser: PropTypes.func,
  onViewUser: PropTypes.func
};

export default EmployeeActions;